import { useCallback, useEffect, useState } from "react";
import { Eye, MousePointerClick, Timer, Users, RefreshCw, BarChart3 } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATH_NAMES = {
    "/": "Início",
    "/servicos": "Serviços",
    "/tributacao": "Tributação e Agenda",
    "/reforma": "Reforma Tributária",
    "/noticias": "Notícias",
    "/contato": "Contato",
    "/metricas": "Métricas",
};

const LABEL_NAMES = {
    whatsapp: "Cliques no WhatsApp",
    "form-submit": "Orçamentos enviados",
    "chat-open": "Aberturas do assistente",
    call: "Cliques para ligar",
    diagnosis: "Diagnóstico gratuito",
};

const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}min ${s}s` : `${s}s`;
};

export const MetricasPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/analytics/summary`);
            setData(await res.json());
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const pageViews = Object.entries(data?.page_views || {}).sort((a, b) => b[1] - a[1]);
    const interactions = Object.entries(data?.interactions || {}).sort((a, b) => b[1] - a[1]);
    const maxPageViews = pageViews.length ? pageViews[0][1] : 1;
    const maxInteractions = interactions.length ? interactions[0][1] : 1;

    const stats = [
        { icon: Users, label: "Visitas únicas", value: data?.total_visits ?? 0, testid: "metric-visits" },
        { icon: Eye, label: "Páginas visualizadas", value: data?.total_page_views ?? 0, testid: "metric-pageviews" },
        { icon: MousePointerClick, label: "Interações", value: data?.total_interactions ?? 0, testid: "metric-interactions" },
        { icon: Timer, label: "Tempo médio no site", value: formatDuration(data?.avg_seconds ?? 0), testid: "metric-avgtime" },
    ];

    return (
        <main className="min-h-screen bg-gradient-to-b from-white to-[#F8FAFC]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                    <div>
                        <p className="eyebrow text-xs font-bold uppercase tracking-[0.2em] text-blue-900" data-testid="metrics-eyebrow">
                            Painel WBS
                        </p>
                        <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="metrics-title">
                            Métricas do site
                        </h1>
                        <p className="mt-3 text-base text-slate-600" data-testid="metrics-subtitle">
                            Visitas, páginas mais vistas, interações e tempo de permanência dos visitantes.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        data-testid="metrics-refresh-button"
                        className="shrink-0 inline-flex items-center gap-2 text-sm font-bold text-blue-900 hover:text-blue-950 border border-blue-300 hover:border-blue-400 rounded-lg px-4 py-2.5 transition-colors duration-200 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        Atualizar
                    </button>
                </div>

                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="metrics-cards">
                    {stats.map((stat) => (
                        <div key={stat.label} className="glass-card rounded-2xl p-7" data-testid={stat.testid}>
                            <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-slate-900 text-amber-400">
                                <stat.icon className="w-5 h-5" />
                            </span>
                            {loading && !data ? (
                                <Skeleton className="mt-5 h-9 w-24" />
                            ) : (
                                <p className="mt-5 text-3xl font-bold tracking-tight text-slate-900">{stat.value}</p>
                            )}
                            <p className="mt-1 text-sm font-semibold text-slate-500">{stat.label}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-10 grid lg:grid-cols-2 gap-6">
                    <div className="glass-card rounded-2xl p-8" data-testid="metrics-pages-panel">
                        <h2 className="flex items-center gap-2.5 text-lg font-semibold text-slate-900">
                            <BarChart3 className="w-5 h-5 text-blue-900" />
                            Páginas mais vistas
                        </h2>
                        <div className="mt-6 space-y-4">
                            {loading && !data ? (
                                [...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)
                            ) : pageViews.length ? (
                                pageViews.map(([path, count]) => (
                                    <div key={path} data-testid={`metrics-page-${path.replace(/\//g, "") || "home"}`}>
                                        <div className="flex justify-between text-sm mb-1.5">
                                            <span className="font-semibold text-slate-700">{PATH_NAMES[path] || path}</span>
                                            <span className="text-slate-500">{count}</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-700"
                                                style={{ width: `${Math.max(6, (count / maxPageViews) * 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-slate-400" data-testid="metrics-pages-empty">Ainda sem registros de páginas.</p>
                            )}
                        </div>
                    </div>

                    <div className="glass-card rounded-2xl p-8" data-testid="metrics-interactions-panel">
                        <h2 className="flex items-center gap-2.5 text-lg font-semibold text-slate-900">
                            <MousePointerClick className="w-5 h-5 text-blue-900" />
                            Interações dos visitantes
                        </h2>
                        <div className="mt-6 space-y-4">
                            {loading && !data ? (
                                [...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)
                            ) : interactions.length ? (
                                interactions.map(([label, count]) => (
                                    <div key={label} data-testid={`metrics-interaction-${label}`}>
                                        <div className="flex justify-between text-sm mb-1.5">
                                            <span className="font-semibold text-slate-700">{LABEL_NAMES[label] || label}</span>
                                            <span className="text-slate-500">{count}</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-slate-700 to-slate-500 transition-all duration-700"
                                                style={{ width: `${Math.max(6, (count / maxInteractions) * 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-slate-400" data-testid="metrics-interactions-empty">Ainda sem interações registradas.</p>
                            )}
                        </div>
                    </div>
                </div>

                <p className="mt-8 text-xs text-slate-400" data-testid="metrics-note">
                    Métricas coletadas pelo próprio site, sem cookies de terceiros. O tempo médio é estimado a cada 30 segundos de navegação ativa.
                </p>
            </div>
        </main>
    );
};
